import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useMaintenanceStatus } from "@/lib/maintenance";
import MaintenancePage from "@/components/MaintenancePage";

import Landing from "@/pages/Landing";
import AffidavitsIndex from "@/pages/AffidavitsIndex";
import { LoginPage, SignupPage, AuthComplete } from "@/pages/Auth";
import Disclaimer from "@/pages/Disclaimer";
import Privacy from "@/pages/Privacy";

import CustomerLayout from "@/pages/customer/CustomerLayout";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import CustomerProfile from "@/pages/customer/CustomerProfile";
import NewDocument from "@/pages/customer/NewDocument";
import Wizard from "@/pages/customer/Wizard";
import TransactionDetail from "@/pages/customer/TransactionDetail";
import CartPage from "@/pages/customer/CartPage";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
// One file per admin page (split out of the former 1852-line AdminPages.jsx); shared helpers
// and the txnGates/gateReason client-side gate mirror live in @/pages/admin/lib.
import { AdminUsers } from "@/pages/admin/AdminUsers";
import { AdminUserDetail, AdminReviewerDetail } from "@/pages/admin/AdminPersonDetail";
import { AdminReviewers } from "@/pages/admin/AdminReviewers";
import { AdminTransactions } from "@/pages/admin/AdminTransactions";
import { AdminTransactionDetail } from "@/pages/admin/AdminTransactionDetail";
import { AdminPayments } from "@/pages/admin/AdminPayments";
import { AdminLedger } from "@/pages/admin/AdminLedger";
import { AdminPricing } from "@/pages/admin/AdminPricing";
import { AdminTemplates } from "@/pages/admin/AdminTemplates";
import { AdminSettings } from "@/pages/admin/AdminSettings";
import { AdminDisasterRecovery } from "@/pages/admin/AdminDisasterRecovery";
import { AdvocateLayout, AdvocateDashboard, AdvocateCase } from "@/pages/advocate/AdvocatePages";

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  // Admin is never polled or blocked — someone has to be able to run the restore/migration a
  // window exists for. Checked ahead of the user/redirect logic below so a logged-out visitor
  // heading straight for a customer/advocate route sees the maintenance page too, not a login form
  // that would only fail once submitted. See core/maintenance.py and backend/CLAUDE.md
  // "Disaster recovery".
  const maintenance = useMaintenanceStatus(role !== "admin");
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Loading...</div>;
  if (role !== "admin" && maintenance?.active) {
    return <MaintenancePage note={maintenance.note} endAt={maintenance.end_at} />;
  }
  if (!user) return <Navigate to={role === "admin" ? "/admin/login" : role === "reviewer" ? "/advocate/login" : "/login"} replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* `closeButton`: every toast carries an X, so a message like "Transaction ID copied" can be
            dismissed on the spot instead of sitting there for its full dwell. */}
        <Toaster position="bottom-right" richColors closeButton />
        <Routes>
          <Route path="/" element={<Landing />} />
          {/* No public /affidavits route. It existed so one landing-page card had somewhere
              to go; the cards are display-only now, and the catalogue lives behind sign-in
              at /customer/affidavits, which is the same component. */}
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/login" element={<LoginPage role="customer" subtitle="Customer Portal" title="Welcome back"/>} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/auth/complete" element={<AuthComplete />} />
          <Route path="/admin/login" element={<LoginPage role="admin" subtitle="Administrator" title="Restricted access"/>} />
          <Route path="/advocate/login" element={<LoginPage role="reviewer" subtitle="Advocate Portal" title="Sign in to your workspace"/>} />

          <Route path="/customer" element={<Protected role="customer"><CustomerLayout/></Protected>}>
            <Route index element={<CustomerDashboard/>} />
            <Route path="new" element={<NewDocument/>} />
            <Route path="profile" element={<CustomerProfile/>} />
            <Route path="affidavits" element={<AffidavitsIndex/>} />
            {/* Generic group picker — any group with more than one template gets a page here
                without a code change. /customer/affidavits stays as the familiar alias. */}
            <Route path="documents/:group" element={<AffidavitsIndex/>} />
            <Route path="cart" element={<CartPage/>} />
            <Route path="txn/:id" element={<TransactionDetail/>} />
            {/* Inside the layout, not alongside it: the wizard used to be a standalone full-screen
                route, so starting a document dropped the left menu and the only way back was its
                own "← Back to dashboard" link. CustomerLayout renders it full-bleed (see
                `fullBleed` there) so the two-pane form/preview split still gets the full width. */}
            <Route path="wizard/:serviceType" element={<Wizard/>} />
          </Route>

          <Route path="/admin" element={<Protected role="admin"><AdminLayout/></Protected>}>
            <Route index element={<AdminAnalytics/>} />
            <Route path="users" element={<AdminUsers/>} />
            <Route path="users/:id" element={<AdminUserDetail/>} />
            <Route path="reviewers" element={<AdminReviewers/>} />
            <Route path="reviewers/:id" element={<AdminReviewerDetail/>} />
            <Route path="transactions" element={<AdminTransactions/>} />
            <Route path="txn/:id" element={<AdminTransactionDetail/>} />
            <Route path="payments" element={<AdminPayments/>} />
            <Route path="pricing" element={<AdminPricing/>} />
            {/* No /admin/orders. Cart Orders showed the same purchases as Payments from the
                other side; the two are merged into Payments & Orders, which also lists the
                failed attempts an orders-only view could never show. */}
            <Route path="templates" element={<AdminTemplates/>} />
            {/* Not /admin/backup — the previous feature at that path was withdrawn (see
                routers/admin.py). This is its replacement: a UI trigger over a CLI-authorized
                restore, not the same shape reopened. See backend/CLAUDE.md "Disaster recovery". */}
            <Route path="disaster-recovery" element={<AdminDisasterRecovery/>} />
            <Route path="ledger" element={<AdminLedger/>} />
            <Route path="settings" element={<AdminSettings/>} />
          </Route>

          <Route path="/advocate" element={<Protected role="reviewer"><AdvocateLayout/></Protected>}>
            <Route index element={<AdvocateDashboard/>} />
            <Route path="case/:id" element={<AdvocateCase/>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
